import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { ActivatedRoute } from '@angular/router'
import { collection, collectionData, doc, docData, Firestore, limit, orderBy, query, where } from '@angular/fire/firestore'
import { Subscription } from 'rxjs'
import { TypeaheadMatch } from 'ngx-bootstrap/typeahead'
import { Client, PaymentMethod, PaymentMethodStatus, paymentMethodsPath } from '@parkupp/core'
import { ClientService } from 'src/app/services/client.service'
import { BackendService } from 'src/app/services/backend.service'

interface LabLogEntry {
    step: string
    ok: boolean
    message: string
    at: Date
    raw?: any
}

/**
 * PayGate Lab — exercises the whole card lifecycle on one page so we can validate the
 * PayWeb3 redirect migration end to end against live PayGate.
 *
 * Steps: tokenise via PayGate's hosted page -> authorise a hold on the token
 * (with or without CVV) -> settle or void that hold.
 */
@Component({
    selector: 'app-admin-paygate-lab',
    templateUrl: './admin-paygate-lab.component.html',
    styleUrls: ['./admin-paygate-lab.component.scss'],
})
export class AdminPaygateLabComponent implements OnInit, OnDestroy, AfterViewInit {
    @ViewChild('payWeb3Form') payWeb3Form!: ElementRef<HTMLFormElement>

    clients: Client[] = []
    /**
     * Typeahead matched on firstName only, so surnames and IDs found nothing. Each option
     * carries a `search` blob (name + company + email + document ID) that the typeahead
     * matches against, with the original client kept alongside for selection.
     */
    clientOptions: { client: Client; search: string; name: string; detail: string }[] = []
    clientSearchText = ''
    selectedClient: Client | null = null

    paymentMethods: PaymentMethod[] = []
    paymentMethodsLoaded = false
    selectedPaymentMethod: PaymentMethod | null = null

    // Step 1 — tokenise
    tokeniseAmountRands = 1
    tokenising = false
    showFrame = false
    labSessionKey: string | null = null
    labSession: any = null
    payWeb3 = { processUrl: '', payRequestId: '', checksum: '' }

    // Step 2 — authorise
    holdAmountRands = 1
    readonly sendCvv = false
    // Undocumented in PayGate's PDF but present in the live WSDL — candidate ways to
    // charge a stored token without a CVV.
    cvvToggle = false
    recurringType = ''
    initiatedType = 'MERCHANT'
    initialAuth = false
    originalRecurringReferenceId = ''
    authorising = false
    lookingUp = false
    recentSessions: any[] = []
    completingSession: string | null = null
    transactionId: string | null = null

    // Steps 3/4 — settle / void
    settling = false
    voiding = false

    log: LabLogEntry[] = []
    showRawFor: Record<number, boolean> = {}

    readonly maxAmountRands = 10

    private clientsSub: Subscription | null = null
    private pmSub: Subscription | null = null
    private sessionSub: Subscription | null = null
    private sessionsListSub: Subscription | null = null

    constructor(
        private clientService: ClientService,
        private backendService: BackendService,
        private firestore: Firestore,
        private route: ActivatedRoute
    ) {}

    ngOnInit(): void {
        this.clientsSub = this.clientService.list().subscribe((clients) => {
            this.clients = clients
            this.clientOptions = clients.map((client) => {
                const name = [client.firstName, client.lastName].filter(Boolean).join(' ').trim() || '(no name)'
                const detail = [client.companyName, client.$email, client.$key].filter(Boolean).join(' · ')
                return {
                    client,
                    name,
                    detail,
                    search: [client.firstName, client.lastName, client.companyName, client.$email, client.$key]
                        .filter(Boolean)
                        .join(' '),
                }
            })
        })

        // We land back here inside the iframe after PayGate redirects; the parent page is
        // already watching the session, so this only matters on a full-page return.
        const sessionKey = this.route.snapshot.queryParamMap.get('session')
        if (sessionKey) this.watchSession(sessionKey)
    }

    ngAfterViewInit(): void {
        // no-op; the PayWeb3 form is submitted on demand
    }

    ngOnDestroy(): void {
        this.clientsSub?.unsubscribe()
        this.pmSub?.unsubscribe()
        this.sessionSub?.unsubscribe()
        this.sessionsListSub?.unsubscribe()
    }

    // --- client + card selection -------------------------------------------------

    onClientSelect(event: TypeaheadMatch): void {
        const option = event.item as { client: Client }
        this.selectedClient = option.client
        this.clientSearchText = [this.selectedClient.firstName, this.selectedClient.lastName].filter(Boolean).join(' ')
        this.resetChain()
        this.loadPaymentMethods(this.selectedClient.$key!)
    }

    private loadPaymentMethods(clientKey: string): void {
        this.pmSub?.unsubscribe()
        this.paymentMethodsLoaded = false
        this.paymentMethods = []
        this.selectedPaymentMethod = null

        this.watchRecentSessions(clientKey)

        const ref = collection(this.firestore, paymentMethodsPath(clientKey))
        this.pmSub = collectionData(ref, { idField: 'documentId' }).subscribe((docs: any[]) => {
            const cards: PaymentMethod[] = []
            docs.forEach((d) => {
                const pm = new PaymentMethod(d)
                pm.$key = d.documentId
                if (pm.status === PaymentMethodStatus.active) cards.push(pm)
            })
            this.paymentMethods = cards
            if (!this.selectedPaymentMethod) this.selectedPaymentMethod = cards.find((c) => c.primary) || cards[0] || null
            this.paymentMethodsLoaded = true
        })
    }

    /**
     * Card captures where PayGate took the payment but the return leg never came back
     * (closed tab, dropped tunnel) sit here as 'pending'. The money has already moved, so
     * the result is still retrievable from PayGate.
     */
    private watchRecentSessions(clientKey: string): void {
        this.sessionsListSub?.unsubscribe()
        const ref = query(
            collection(this.firestore, 'paygateLabSessions'),
            where('clientKey', '==', clientKey),
            orderBy('createdAt', 'desc'),
            limit(5)
        )
        this.sessionsListSub = collectionData(ref, { idField: 'documentId' }).subscribe((docs: any[]) => {
            this.recentSessions = docs
        })
    }

    async completeSession(sessionKey: string): Promise<void> {
        this.completingSession = sessionKey
        const response = await this.backendService.httpLabCompleteSession(sessionKey)
        this.completingSession = null
        this.addLog('Recover pending card capture', !response?.error, response?.message || 'No message', response)
        if (response?.clientKey) this.loadPaymentMethods(response.clientKey)
    }

    selectCard(pm: PaymentMethod): void {
        this.selectedPaymentMethod = pm
        this.transactionId = null
    }

    private resetChain(): void {
        this.selectedPaymentMethod = null
        this.transactionId = null
        this.labSession = null
        this.labSessionKey = null
        this.showFrame = false
    }

    // --- step 1: tokenise via PayGate's hosted page --------------------------------

    get isTokeniseAmountValid(): boolean {
        return this.tokeniseAmountRands >= 0.01 && this.tokeniseAmountRands <= this.maxAmountRands
    }

    async startTokenise(): Promise<void> {
        if (!this.selectedClient || !this.isTokeniseAmountValid) return

        this.tokenising = true
        this.labSession = null

        const response = await this.backendService.httpLabTokeniseInit(
            this.selectedClient.$key!,
            Math.round(this.tokeniseAmountRands * 100)
        )

        this.tokenising = false

        if (response?.error) {
            this.addLog('Tokenise — initiate', false, response.message || 'Failed to start PayWeb3 request', response)
            return
        }

        this.addLog('Tokenise — initiate', true, `PAY_REQUEST_ID ${response.payRequestId}`, response)

        this.payWeb3 = {
            processUrl: response.processUrl,
            payRequestId: response.payRequestId,
            checksum: response.checksum,
        }
        this.labSessionKey = response.sessionKey
        this.showFrame = true
        this.watchSession(response.sessionKey)

        // Post into the named iframe once Angular has rendered the form with its values.
        setTimeout(() => this.payWeb3Form?.nativeElement?.submit(), 0)
    }

    private watchSession(sessionKey: string): void {
        this.sessionSub?.unsubscribe()
        this.labSessionKey = sessionKey
        const ref = doc(this.firestore, `paygateLabSessions/${sessionKey}`)
        this.sessionSub = docData(ref).subscribe((data: any) => {
            if (!data) return
            const wasComplete = this.labSession?.status === 'complete'
            this.labSession = data

            if (data.status === 'complete' && !wasComplete) {
                this.showFrame = false
                const ok = !!data.vaultId
                this.addLog(
                    'Tokenise — result',
                    ok,
                    ok
                        ? `Vaulted ${data.maskedCard} (token ${data.vaultId})`
                        : data.queryMessage || data.resultDesc || `TRANSACTION_STATUS ${data.transactionStatus}`,
                    { returnRaw: data.returnRaw, queryRaw: data.queryRaw }
                )
                if (data.clientKey) this.loadPaymentMethods(data.clientKey)
            }
        })
    }

    cancelFrame(): void {
        this.showFrame = false
    }

    // --- step 2: authorise a hold on the token -------------------------------------

    get isHoldAmountValid(): boolean {
        return this.holdAmountRands >= 0.01 && this.holdAmountRands <= this.maxAmountRands
    }

    async authorise(): Promise<void> {
        if (!this.selectedClient || !this.selectedPaymentMethod || !this.isHoldAmountValid) return

        this.authorising = true
        const response = await this.backendService.httpLabTokenPayment({
            clientKey: this.selectedClient.$key!,
            paymentMethodKey: this.selectedPaymentMethod.$key!,
            amountInCents: Math.round(this.holdAmountRands * 100),
            sendCvv: this.sendCvv,
            cvvToggle: this.cvvToggle,
            recurringType: this.recurringType || undefined,
            initiatedType: this.recurringType ? this.initiatedType : undefined,
            initialAuth: this.recurringType ? this.initialAuth : undefined,
            originalRecurringReferenceId: this.originalRecurringReferenceId || undefined,
        })
        this.authorising = false

        this.transactionId = response?.transactionId || null

        const flags = [
            `CVV ${this.sendCvv ? 'sent' : 'omitted'}`,
            this.cvvToggle ? 'CvvToggle' : null,
            this.recurringType ? `${this.recurringType}/${this.initiatedType}` : null,
        ].filter(Boolean).join(', ')

        this.addLog(`Authorise (hold) — ${flags}`, !response?.error, response?.message || 'No message', response)
    }

    async lookupVault(): Promise<void> {
        if (!this.selectedClient || !this.selectedPaymentMethod) return
        this.lookingUp = true
        const response = await this.backendService.httpLabLookupVault(this.selectedClient.$key!, this.selectedPaymentMethod.$key!)
        this.lookingUp = false
        this.addLog('Vault lookup (which account owns this token?)', !response?.error, response?.message || 'No message', response)
    }

    // --- steps 3/4: settle or void --------------------------------------------------

    async settle(): Promise<void> {
        if (!this.transactionId) return
        this.settling = true
        const response = await this.backendService.httpLabSettle(this.transactionId)
        this.settling = false
        this.addLog('Settle (capture)', !response?.error, response?.message || 'No message', response)
    }

    async voidHold(): Promise<void> {
        if (!this.transactionId) return
        this.voiding = true
        const response = await this.backendService.httpLabVoid(this.transactionId)
        this.voiding = false
        this.addLog('Void (release)', !response?.error, response?.message || 'No message', response)
    }

    // --- log ------------------------------------------------------------------------

    private addLog(step: string, ok: boolean, message: string, raw?: any): void {
        this.log.unshift({ step, ok, message, at: new Date(), raw })
    }

    toggleRaw(index: number): void {
        this.showRawFor[index] = !this.showRawFor[index]
    }

    asJson(value: any): string {
        return JSON.stringify(value, null, 2)
    }

    clearLog(): void {
        this.log = []
        this.showRawFor = {}
    }
}
